// src/views/indicators/IndicatorsPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    CAlert,
    CBadge,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CCol,
    CForm,
    CFormInput,
    CFormLabel,
    CFormSelect,
    CFormTextarea,
    CModal,
    CModalBody,
    CModalFooter,
    CModalHeader,
    CModalTitle,
    CRow,
    CSpinner,
    CTable,
    CTableBody,
    CTableHead,
    CTableHeaderCell,
    CTableRow,
    CTableDataCell,
    CTooltip,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
    cilCalculator,
    cilClone,
    cilCloudDownload,
    cilCloudUpload,
    cilPencil,
    cilPlus,
    cilTrash,
} from '@coreui/icons'

import { apiClient } from 'services/Api'
import ToastNotification from 'components/globals/ToastNotification'

const GROUPS = [
    { value: 'MP-01', label: 'Planeamento e Avaliação do Sistema de Gestão (MP-01)' },
    { value: 'MP-02', label: 'Gestão de Processos de Suporte (MP-02)' },
    { value: 'MP-03', label: 'Gestão de Comercial (MP-03)' },
    { value: 'MP-04', label: 'Elaboração e Gestão de Projetos com Incentivos (MP-04)' },
    { value: 'MP-05', label: 'Elaboração e Gestão de Projetos sem Incentivos (MP-05)' },
    { value: 'MP-06', label: 'Gestão da Inovação (MP-06)' },
]

const currentYear = () => new Date().getFullYear()

const buildYearOptions = () => {
    const start = 2019
    const end = currentYear() + 1
    const years = []
    for (let y = end; y >= start; y -= 1) years.push(y) // desc
    return years
}

const emptyForm = {
    year: currentYear(),
    group: 'MP-01',
    description: '',
    key: '',
    calc_group: '',
    value_type: 'raw',
    inverse_relation: false,
    objective: '',
    obtained: '',
    next_year: '',
    obs: '',
    auto: false,
    order: 0,
}

function groupLabel(value) {
    return GROUPS.find((g) => g.value === value)?.label || value || ''
}

function toNumberOrNull(v) {
    if (v === '' || v === null || v === undefined) return null
    const n = Number(v)
    return Number.isFinite(n) ? n : null
}

function normalizeStringOrNull(s) {
    const v = (s ?? '').trim()
    return v ? v : null
}

function formatDecimal(v) {
    if (v === null || v === undefined || v === '') return ''
    return String(v)
}

function normalizeText(value) {
    if (!value) return ''
    return value
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
}

function isPaymentDaysIndicator(row) {
    const key = (row?.key || '').toLowerCase()
    if (key === 'mp02_dias_recebimento' || key === 'mp02_dias_recebimento_clientes') return true
    const desc = normalizeText(row?.description || '')
    return desc.includes('dias de recebimento')
}

const PT_INT = new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 0 })
const PT_NUM = new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 2 })
const PT_CURRENCY = new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 2,
})

const RESULT_CURRENCY_KEYS = new Set([
    'mp04_montante_si_inov',
    'mp04_moldes',
    'mp04_aeronautica',
    'mp04_metalomecanica',
    'mp04_pedra_natural',
    'mp04_vidro',
    'mp04_textil',
    'mp04_turismo',
    'mp04_madeiras',
    'mp04_outros',
])

const RESULT_PERCENT_KEYS = new Set([
    'mp01_objectives',
    'mp01_eficacia_acoes',
    'mp02_eficacia_formacao',
    'mp02_satisfacao_colabs',
    'mp03_satisfacao_clientes',
    'mp04_projetos_aprovados',
    'mp04_investimento_aprovado',
    'mp05_desvio',
    'mp05_desvio_horas',
    'mp06_ideias',
    'mp06_cumprimento_vigilancias',
    'mp06_concretizacao_idi',
    'mp06_desvio_idi',
])

const RESULT_CURRENCY_HINTS = [
    '€',
    'eur',
    'montante',
    'volume de negócios',
    'faturação',
    'ebitda',
    'custos',
    'custo',
    'orçamento',
]

const RESULT_PERCENT_HINTS = ['%', 'taxa', 'grau', 'cumprimento', 'concretização', 'percent']

function parseNumberSafe(value) {
    if (value === null || value === undefined || value === '') return null
    const n = Number(value)
    return Number.isFinite(n) ? n : null
}

function resolveResultType(row) {
    const key = (row?.key || '').toLowerCase()
    const desc = (row?.description || '').toLowerCase()

    if (desc.includes('%') || RESULT_PERCENT_KEYS.has(key) || RESULT_PERCENT_HINTS.some((h) => desc.includes(h))) {
        return 'percentage'
    }
    if (desc.includes('€') || RESULT_CURRENCY_KEYS.has(key) || RESULT_CURRENCY_HINTS.some((h) => desc.includes(h))) {
        return 'currency'
    }
    return 'raw'
}

function formatIndicatorValue(row, value) {
    const n = parseNumberSafe(value)
    if (n === null) return ''

    const type = row?.value_type || resolveResultType(row)
    if (type === 'currency') return PT_CURRENCY.format(n)
    if (type === 'percentage') return `${PT_NUM.format(n)}%`

    const formatter = Number.isInteger(n) ? PT_INT : PT_NUM
    return formatter.format(n)
}

// Cor de fundo do obtido (Resultado)
function obtainedClass(row) {
    const obj = row?.objective === null || row?.objective === undefined ? null : Number(row?.objective)
    const obt = row?.obtained === null || row?.obtained === undefined ? null : Number(row?.obtained)
    const inverse = !!row?.inverse_relation

    // Se faltar algum dos valores, fica amarelo (indeterminado)
    if (!Number.isFinite(obj) || !Number.isFinite(obt)) return 'bg-warning bg-opacity-25'

    if (inverse) {
        if (obt > obj) return 'bg-danger bg-opacity-25'
        return 'bg-success bg-opacity-25'
    }

    if (obj > obt) return 'bg-danger bg-opacity-25'
    if (obt >= obj) return 'bg-success bg-opacity-25'
    return 'bg-warning bg-opacity-25'
}

export default function IndicatorsPage() {
    const [toast, setToast] = useState(null)

    const [loading, setLoading] = useState(false)
    const [actionLoading, setActionLoading] = useState(false)
    const [calcLoading, setCalcLoading] = useState(false)
    const [pdfLoading, setPdfLoading] = useState(false)
    const [uploadLoading, setUploadLoading] = useState(false)

    const [rows, setRows] = useState([])
    const [error, setError] = useState(null)
    const [calcState, setCalcState] = useState({
        state: 'IDLE',
        taskId: null,
        result: null,
        error: null,
    })
    const calcPollRef = useRef(null)

    // Filters
    const [year, setYear] = useState(currentYear())
    const [search, setSearch] = useState('')

    // Modal / form
    const [modalOpen, setModalOpen] = useState(false)
    const [editing, setEditing] = useState(null)
    const [form, setForm] = useState({ ...emptyForm })
    const [uploadModalOpen, setUploadModalOpen] = useState(false)
    const [uploadTarget, setUploadTarget] = useState(null)
    const [faturasFiles, setFaturasFiles] = useState([])
    const [recibosFiles, setRecibosFiles] = useState([])

    // Delete confirm
    const [deleteTarget, setDeleteTarget] = useState(null)

    // Drag & drop state
    const [dragState, setDragState] = useState({ draggingId: null, fromGroup: null })

    const yearOptions = useMemo(() => buildYearOptions(), [])

    const showSuccess = useCallback((message) => {
        setToast({ title: 'Indicadores', message, type: 'success' })
    }, [])

    const showError = useCallback((message) => {
        setToast({ title: 'Indicadores', message, type: 'error' })
    }, [])

    const [permissions, setPermissions] = useState({
        can_create: false,
        can_view: true,
        can_update: false,
        can_delete: false,
    })
    const canCreate = !!permissions?.can_create
    const canUpdate = !!permissions?.can_update
    const canDelete = !!permissions?.can_delete

    const clearCalcPolling = useCallback(() => {
        if (calcPollRef.current) {
            clearInterval(calcPollRef.current)
            calcPollRef.current = null
        }
    }, [])


    const fetchIndicators = useCallback(async () => {
        setLoading(true)
        setError(null)

        try {
            const params = new URLSearchParams()
            params.set('year', String(year))
            if (search) params.set('search', search)

            const response = await apiClient.get(`/indicators/?${params.toString()}`)
            const data = response.data || {}
            setRows(Array.isArray(data.indicators) ? data.indicators : [])
            if (data.permissions) setPermissions(data.permissions)
        } catch (e) {
            setRows([])
            setError(e?.response?.data || { detail: 'Falha ao carregar indicadores.' })
        } finally {
            setLoading(false)
        }
    }, [year, search])

    const pollCalcStatus = useCallback(
        async (taskId) => {
            if (!taskId) return
            try {
                const response = await apiClient.get(
                    `/indicators/calculate-year-status/?task_id=${encodeURIComponent(taskId)}`,
                )
                const data = response.data || {}
                const state = data.state || 'PENDING'

                setCalcState({
                    state,
                    taskId,
                    result: data.result || null,
                    error: data.error || null,
                })

                if (state === 'SUCCESS') {
                    clearCalcPolling()
                    setCalcLoading(false)
                    await fetchIndicators()
                } else if (state === 'FAILURE') {
                    clearCalcPolling()
                    setCalcLoading(false)
                    const errMsg = data?.error ? `Recalculo falhou: ${data.error}` : 'Recalculo falhou.'
                    showError(errMsg)
                }
                return state
            } catch (err) {
                clearCalcPolling()
                setCalcLoading(false)
                showError('Falha ao verificar o estado do recalculo.')
                return null
            }
        },
        [clearCalcPolling, fetchIndicators, showError],
    )

    const startRecalculateYear = useCallback(async () => {
        if (!year) return
        setCalcLoading(true)
        setCalcState({ state: 'QUEUED', taskId: null, result: null, error: null })
        clearCalcPolling()

        try {
            const response = await apiClient.post(`/indicators/calculate-year/`, { year: Number(year) })
            const data = response.data || {}
            const taskId = data.task_id

            if (!taskId) {
                setCalcLoading(false)
                showError('Resposta invalida ao iniciar recalculo.')
                return
            }

            setCalcState({
                state: data.state || 'PENDING',
                taskId,
                result: null,
                error: null,
            })

            const state = await pollCalcStatus(taskId)
            if (state && state !== 'SUCCESS' && state !== 'FAILURE') {
                calcPollRef.current = setInterval(() => pollCalcStatus(taskId), 10000)
            }
        } catch (err) {
            setCalcLoading(false)
            const msg = err?.response?.data?.detail || 'Falha ao iniciar recalculo.'
            showError(msg)
        }
    }, [clearCalcPolling, pollCalcStatus, showError, year])

    useEffect(() => {
        fetchIndicators()
    }, [fetchIndicators])

    useEffect(() => {
        return () => clearCalcPolling()
    }, [clearCalcPolling])

    useEffect(() => {
        setCalcState({ state: 'IDLE', taskId: null, result: null, error: null })
        clearCalcPolling()
    }, [year, clearCalcPolling])

    const errorText = useMemo(() => {
        if (!error) return null
        if (typeof error === 'string') return error
        if (error.detail) return error.detail
        if (error.non_field_errors?.length) return error.non_field_errors.join(' ')
        const firstKey = Object.keys(error).find((k) => Array.isArray(error[k]) && error[k].length)
        if (firstKey) return `${firstKey}: ${error[firstKey].join(' ')}`
        return 'Pedido falhou.'
    }, [error])

    const calcInProgress = useMemo(() => {
        return ['PENDING', 'RECEIVED', 'STARTED', 'RETRY', 'QUEUED'].includes(calcState.state)
    }, [calcState.state])

    const calcStatusText = useMemo(() => {
        if (calcState.state === 'FAILURE') {
            return calcState.error ? `Recalculo falhou: ${calcState.error}` : 'Recalculo falhou.'
        }
        if (calcInProgress) {
            return 'A recalcular…'
        }
        return null
    }, [calcInProgress, calcState.error, calcState.state])

    // Agrupar por grupo e ordenar por order/id
    const rowsByGroup = useMemo(() => {
        const map = {}
        for (const g of GROUPS) map[g.value] = []
        for (const r of rows) {
            if (!map[r.group]) map[r.group] = []
            map[r.group].push(r)
        }
        for (const g of Object.keys(map)) {
            map[g].sort((a, b) => {
                const ao = Number(a.order ?? 0)
                const bo = Number(b.order ?? 0)
                if (ao !== bo) return ao - bo
                return Number(a.id ?? 0) - Number(b.id ?? 0)
            })
        }
        return map
    }, [rows])

    function openCreate() {
        setEditing(null)
        setForm({
            ...emptyForm,
            year: year || currentYear(),
            group: 'MP-01',
            auto: false,
            key: '',
            calc_group: '',
        })
        setModalOpen(true)
    }

    function openEdit(row) {
        setEditing(row)
        setForm({
            year: row.year,
            group: row.group,
            description: row.description || '',
            key: row.key || '',
            calc_group: row.calc_group || '',
            value_type: row.value_type || 'raw',
            inverse_relation: !!row.inverse_relation,
            objective: formatDecimal(row.objective),
            obtained: formatDecimal(row.obtained),
            next_year: formatDecimal(row.next_year),
            obs: row.obs || '',
            auto: !!row.auto,
            order: row.order ?? 0,
        })
        setModalOpen(true)
    }

    function closeModal() {
        setModalOpen(false)
        setEditing(null)
        setForm({ ...emptyForm, year: year || currentYear() })
    }

    function openUpload(row) {
        setUploadTarget(row)
        setFaturasFiles([])
        setRecibosFiles([])
        setUploadModalOpen(true)
    }

    function closeUpload() {
        setUploadModalOpen(false)
        setUploadTarget(null)
        setFaturasFiles([])
        setRecibosFiles([])
    }

    const submitUpload = useCallback(
        async (e) => {
            e?.preventDefault?.()
            if (!uploadTarget?.id) return

            if (faturasFiles.length === 0 || recibosFiles.length === 0) {
                setError({ detail: 'Seleciona 1 ou mais ficheiros de faturas e pelo menos 1 de recibos.' })
                return
            }

            setUploadLoading(true)
            setError(null)

            try {
                const formData = new FormData()
                formData.append('indicator_id', String(uploadTarget.id))
                faturasFiles.forEach((file) => formData.append('faturas', file))
                recibosFiles.forEach((file) => formData.append('recibos', file))

                const response = await apiClient.post('/indicators/payment-days/', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })

                const data = response.data || {}
                showSuccess(`Média calculada: ${data.average_days} dias.`)
                closeUpload()
                await fetchIndicators()
            } catch (err) {
                setError(err?.response?.data || { detail: 'Falha ao calcular dias de recebimento.' })
            } finally {
                setUploadLoading(false)
            }
        },
        [faturasFiles, fetchIndicators, recibosFiles, uploadTarget],
    )

    const submitForm = useCallback(
        async (e) => {
            e?.preventDefault?.()
            setActionLoading(true)
            setError(null)

            const payload = {
                year: Number(form.year),
                group: form.group,
                description: (form.description || '').trim(),
                value_type: form.value_type || 'raw',
                inverse_relation: !!form.inverse_relation,
                objective: toNumberOrNull(form.objective),
                obtained: toNumberOrNull(form.obtained),
                next_year: toNumberOrNull(form.next_year),
                obs: normalizeStringOrNull(form.obs),
                order: Number(form.order ?? 0),
                auto: !!form.auto,
                key: normalizeStringOrNull(form.key),
                calc_group: normalizeStringOrNull(form.calc_group),
            }

            if (editing?.id) {
                delete payload.year
            }

            try {
                if (editing?.id) {
                    await apiClient.put(`/indicators/${editing.id}/`, payload)
                    showSuccess('Indicador atualizado.')
                } else {
                    await apiClient.post(`/indicators/`, payload)
                    showSuccess('Indicador criado.')
                }
                closeModal()
                await fetchIndicators()
            } catch (err) {
                setError(err?.response?.data || { detail: 'Falha ao gravar.' })
            } finally {
                setActionLoading(false)
            }
        },
        [editing, fetchIndicators, form],
    )

    const duplicatePreviousYear = useCallback(async () => {
        setActionLoading(true)
        setError(null)

        try {
            const response = await apiClient.post(`/indicators/duplicate-previous-year/`, { year: Number(year) })
            const data = response.data || {}
            showSuccess(data.detail || 'Indicadores gerados.')
            await fetchIndicators()
        } catch (err) {
            setError(err?.response?.data || { detail: 'Falha ao gerar indicadores.' })
        } finally {
            setActionLoading(false)
        }
    }, [fetchIndicators, year])

    const deleteRow = useCallback(
        async (id) => {
            setActionLoading(true)
            setError(null)

            try {
                await apiClient.delete(`/indicators/${id}/`)
                showSuccess('Indicador apagado.')
                setDeleteTarget(null)
                await fetchIndicators()
            } catch (err) {
                setError(err?.response?.data || { detail: 'Falha ao apagar.' })
            } finally {
                setActionLoading(false)
            }
        },
        [fetchIndicators],
    )

    // --- Drag & drop ---
    const persistGroupOrder = useCallback(
        async (groupCode, newGroupRows) => {
            setActionLoading(true)
            setError(null)

            // Optimistic update: update local rows + orders immediately
            setRows((prev) => {
                const ids = newGroupRows.map((r) => r.id)
                const rank = new Map(ids.map((id, i) => [id, i + 1])) // order starts at 1
                return prev.map((r) =>
                    r.group === groupCode && rank.has(r.id)
                        ? { ...r, order: rank.get(r.id) }
                        : r,
                )
            })

            try {
                const ordered_ids = newGroupRows.map((r) => r.id)

                await apiClient.post(`/indicators/bulk-reorder/`, {
                    year: Number(year),
                    group: groupCode,
                    ordered_ids,
                })

                // OPTIONAL: if you still want to refresh from server, do it without nuking UX:
                // setTimeout(() => fetchIndicators(), 500)
            } catch (err) {
                setError(err?.response?.data || { detail: 'Falha ao atualizar a ordem.' })
                // Revert by reloading (only on failure)
                await fetchIndicators()
            } finally {
                setActionLoading(false)
            }
        },
        [fetchIndicators, year],
    )


    const onDragStartRow = useCallback((row) => {
        setDragState({ draggingId: row.id, fromGroup: row.group })
    }, [])

    const onDragOverRow = useCallback((e) => {
        e.preventDefault()
    }, [])

    const onDropRow = useCallback(
        async (targetRow) => {
            const { draggingId, fromGroup } = dragState
            if (!draggingId || !fromGroup) return
            if (fromGroup !== targetRow.group) return // só dentro do mesmo grupo
            if (draggingId === targetRow.id) return

            const groupRows = rowsByGroup[fromGroup] || []
            const fromIdx = groupRows.findIndex((r) => r.id === draggingId)
            const toIdx = groupRows.findIndex((r) => r.id === targetRow.id)
            if (fromIdx < 0 || toIdx < 0) return

            const next = [...groupRows]
            const [moved] = next.splice(fromIdx, 1)
            next.splice(toIdx, 0, moved)

            // Persistir
            await persistGroupOrder(fromGroup, next)

            setDragState({ draggingId: null, fromGroup: null })
        },
        [dragState, persistGroupOrder, rowsByGroup],
    )

    const onDragEnd = useCallback(() => {
        setDragState({ draggingId: null, fromGroup: null })
    }, [])

    const exportPdf = useCallback(async () => {
        setPdfLoading(true)
        setError(null)

        try {
            const response = await apiClient.get('/indicators/pdf/', {
                params: {
                    year: Number(year),
                    ...(search ? { search } : {}),
                },
                responseType: 'blob',
                validateStatus: () => true,
            })

            if (response.status !== 200) {
                let message = 'Falha ao gerar PDF.'

                try {
                    const text = await response.data.text()
                    const payload = JSON.parse(text)
                    message = payload?.detail || payload?.error || message
                } catch (parseError) {
                    void parseError
                }

                setError({ detail: message })
                return
            }

            const blob = new Blob([response.data], { type: 'application/pdf' })
            const downloadUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = downloadUrl
            link.download = `matriz-indicadores-${year}.pdf`
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(downloadUrl)
        } catch (err) {
            setError({ detail: 'Falha ao gerar PDF.' })
        } finally {
            setPdfLoading(false)
        }
    }, [search, year])

    return (
        <CRow>
            <CCol xs={12}>
                <CCard className="mb-4">
                    <CCardHeader className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
                        <div className="d-flex flex-column">
                            <strong>Matriz de Indicadores</strong>
                        </div>

                        <div className="d-flex flex-wrap gap-2">
                            <CButton color="primary" variant="outline" onClick={exportPdf} disabled={loading || pdfLoading}>
                                {pdfLoading ? <CSpinner size="sm" className="me-2" /> : <CIcon icon={cilCloudDownload} className="me-2" />}
                                Gerar PDF
                            </CButton>

                            <CTooltip content="Recalcular indicadores automaticamente">
                                <CButton
                                    color="info"
                                    onClick={startRecalculateYear}
                                    disabled={calcLoading || calcInProgress || actionLoading || !year || !canUpdate}
                                >
                                    <CIcon icon={cilCalculator} className="me-2" />
                                    Recalcular
                                </CButton>
                            </CTooltip>

                            <CTooltip content="Gerar indicadores a partir do ano anterior">
                                <CButton color="warning" onClick={duplicatePreviousYear} disabled={actionLoading || loading || !year || !canCreate}>
                                    <CIcon icon={cilClone} className="me-2" />
                                    Gerar indicadores
                                </CButton>
                            </CTooltip>

                            <CButton color="success" onClick={openCreate} disabled={actionLoading || !canCreate}>
                                <CIcon icon={cilPlus} className="me-2" />
                                Novo indicador
                            </CButton>
                        </div>
                    </CCardHeader>

                    <CCardBody>
                        <ToastNotification toast={toast} setToast={setToast} />
                        {errorText && <CAlert color="danger">{errorText}</CAlert>}
                        {calcStatusText && (
                            <CAlert color={calcState.state === 'FAILURE' ? 'danger' : calcState.state === 'SUCCESS' ? 'success' : 'info'}>
                                {calcInProgress && <CSpinner size="sm" className="me-2" />}
                                {calcStatusText}
                            </CAlert>
                        )}

                        <CRow className="g-3 mb-4">
                            <CCol md={4}>
                                <CFormLabel>Ano</CFormLabel>
                                <CFormSelect value={year} onChange={(e) => setYear(Number(e.target.value))}>
                                    {yearOptions.map((y) => (
                                        <option key={y} value={y}>
                                            {y}
                                        </option>
                                    ))}
                                </CFormSelect>
                            </CCol>

                            <CCol md={8}>
                                <CFormLabel>Pesquisa (descrição)</CFormLabel>
                                <CFormInput
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Pesquisar…"
                                />
                            </CCol>
                        </CRow>

                        {loading ? (
                            <div className="d-flex align-items-center gap-2">
                                <CSpinner size="sm" /> A carregar…
                            </div>
                        ) : (
                            GROUPS.map((g) => {
                                const groupRows = rowsByGroup[g.value] || []
                                return (
                                    <CCard key={g.value} className="mb-4">
                                        <CCardHeader className="d-flex align-items-center justify-content-between">
                                            <div>
                                                <strong>{g.label}</strong>
                                            </div>
                                            <CBadge color="secondary">{groupRows.length}</CBadge>
                                        </CCardHeader>

                                        <CCardBody>
                                            <div className="table-responsive">
                                                <CTable hover align="middle">
                                                    <CTableHead>
                                                        <CTableRow>
                                                            <CTableHeaderCell style={{ minWidth: 360 }}>Descrição</CTableHeaderCell>
                                                            <CTableHeaderCell style={{ width: 180 }} className="text-end">
                                                                Meta
                                                            </CTableHeaderCell>
                                                            <CTableHeaderCell style={{ width: 190 }} className="text-end">
                                                                Resultado
                                                            </CTableHeaderCell>
                                                            <CTableHeaderCell style={{ width: 180 }} className="text-end">
                                                                Objetivo <br/> próximo ano
                                                            </CTableHeaderCell>
                                                            <CTableHeaderCell style={{ width: 140 }} />
                                                        </CTableRow>
                                                    </CTableHead>

                                                    <CTableBody>
                                                        {groupRows.length === 0 ? (
                                                            <CTableRow>
                                                                <CTableDataCell colSpan={5} className="text-body-secondary">
                                                                    Sem dados
                                                                </CTableDataCell>
                                                            </CTableRow>
                                                        ) : (
                                                            groupRows.map((row) => (
                                                                <CTableRow
                                                                    key={row.id}
                                                                    draggable={canUpdate && !actionLoading}
                                                                    onDragStart={() => onDragStartRow(row)}
                                                                    onDragOver={onDragOverRow}
                                                                    onDrop={() => onDropRow(row)}
                                                                    onDragEnd={onDragEnd}
                                                                    style={canUpdate ? { cursor: 'grab' } : undefined}
                                                                >
                                                                    <CTableDataCell>
                                                                        <div className="d-flex align-items-start gap-2">
                                                                            {canUpdate && (
                                                                                <span className="text-body-secondary" title="Arrastar para ordenar">
                                                                                    ≡
                                                                                </span>
                                                                            )}
                                                                            <div>
                                                                                <div className="fw-semibold">{row.description} {row.auto ? <CBadge color="info">AUTO</CBadge> : ''}</div>
                                                                                {row.obs ? (
                                                                                    <div className="small text-body-secondary obs-clamp">{row.obs}</div>
                                                                                ) : null}
                                                                            </div>
                                                                        </div>
                                                                    </CTableDataCell>

                                                                    <CTableDataCell className="text-end">
                                                                        {formatIndicatorValue(row, row.objective)}
                                                                    </CTableDataCell>

                                                                    <CTableDataCell className={`text-end ${obtainedClass(row)}`}>
                                                                        {formatIndicatorValue(row, row.obtained)}
                                                                    </CTableDataCell>

                                                                    <CTableDataCell className="text-end">
                                                                        {formatIndicatorValue(row, row.next_year)}
                                                                    </CTableDataCell>

                                                                    <CTableDataCell className="text-end">
                                                                        <div className="d-flex justify-content-end gap-2">
                                                                            {canUpdate && isPaymentDaysIndicator(row) && (
                                                                                <CTooltip content="Calcular dias de recebimento">
                                                                                    <CButton
                                                                                        size="sm"
                                                                                        color="info"
                                                                                        variant="outline"
                                                                                        onClick={() => openUpload(row)}
                                                                                        disabled={actionLoading || uploadLoading}
                                                                                    >
                                                                                        <CIcon icon={cilCloudUpload} />
                                                                                    </CButton>
                                                                                </CTooltip>
                                                                            )}
                                                                            <CTooltip content="Editar">
                                                                                <CButton
                                                                                    size="sm"
                                                                                    color="secondary"
                                                                                    variant="outline"
                                                                                    onClick={() => openEdit(row)}
                                                                                    disabled={actionLoading || !canUpdate}
                                                                                >
                                                                                    <CIcon icon={cilPencil} />
                                                                                </CButton>
                                                                            </CTooltip>

                                                                            <CTooltip content="Apagar">
                                                                                <CButton
                                                                                    size="sm"
                                                                                    color="danger"
                                                                                    variant="outline"
                                                                                    onClick={() => setDeleteTarget(row)}
                                                                                    disabled={actionLoading || !canDelete}
                                                                                >
                                                                                    <CIcon icon={cilTrash} />
                                                                                </CButton>
                                                                            </CTooltip>
                                                                        </div>
                                                                    </CTableDataCell>
                                                                </CTableRow>
                                                            ))
                                                        )}
                                                    </CTableBody>
                                                </CTable>
                                            </div>
                                        </CCardBody>
                                    </CCard>
                                )
                            })
                        )}
                    </CCardBody>
                </CCard>
            </CCol>

            {/* Create/Edit modal */}
            <CModal visible={modalOpen} onClose={closeModal} backdrop="static" alignment="center" size="lg">
                <CModalHeader>
                    <CModalTitle>{editing ? 'Editar indicador' : 'Novo indicador'}</CModalTitle>
                </CModalHeader>

                <CForm onSubmit={submitForm}>
                    <CModalBody>
                        {errorText && <CAlert color="danger">{errorText}</CAlert>}

                        <CRow className="g-3">
                            <CCol md={4}>
                                <CFormLabel>Ano</CFormLabel>

                                {editing ? (
                                    <CFormInput value={form.year} readOnly />
                                ) : (
                                    <CFormSelect
                                        value={form.year}
                                        onChange={(e) => setForm((p) => ({ ...p, year: Number(e.target.value) }))}
                                    >
                                        {yearOptions.map((y) => (
                                            <option key={y} value={y}>
                                                {y}
                                            </option>
                                        ))}
                                    </CFormSelect>
                                )}
                            </CCol>


                            <CCol md={8}>
                                <CFormLabel>Grupo</CFormLabel>
                                <CFormSelect
                                    value={form.group}
                                    onChange={(e) => setForm((p) => ({ ...p, group: e.target.value }))}
                                    required
                                >
                                    {GROUPS.map((g) => (
                                        <option key={g.value} value={g.value}>
                                            {g.label}
                                        </option>
                                    ))}
                                </CFormSelect>
                            </CCol>

                            <CCol xs={12}>
                                <CFormLabel>Descrição</CFormLabel>
                                <CFormInput
                                    value={form.description}
                                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                                    required
                                    maxLength={255}
                                />
                            </CCol>

                            <CCol md={4}>
                                <CFormLabel>Meta</CFormLabel>
                                <CFormInput
                                    value={form.objective}
                                    onChange={(e) => setForm((p) => ({ ...p, objective: e.target.value }))}
                                />
                            </CCol>

                            <CCol md={4}>
                                <CFormLabel>Resultado</CFormLabel>
                                <CFormInput
                                    value={form.obtained}
                                    onChange={(e) => setForm((p) => ({ ...p, obtained: e.target.value }))}
                                    readOnly={!!form.auto}
                                    disabled={!!form.auto}
                                />
                            </CCol>

                            <CCol md={4}>
                                <CFormLabel>Objetivo próximo ano</CFormLabel>
                                <CFormInput
                                    value={form.next_year}
                                    onChange={(e) => setForm((p) => ({ ...p, next_year: e.target.value }))}
                                />
                            </CCol>

                            <CCol md={4}>
                                <CFormLabel>Tipo de valor</CFormLabel>
                                <CFormSelect
                                    value={form.value_type}
                                    onChange={(e) => setForm((p) => ({ ...p, value_type: e.target.value }))}
                                >
                                    <option value="raw">Valor</option>
                                    <option value="currency">Monetário</option>
                                    <option value="percentage">Percentagem</option>
                                </CFormSelect>
                            </CCol>

                            <CCol xs={12}>
                                <div className="form-check">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="inverseCheck"
                                        checked={!!form.inverse_relation}
                                        onChange={(e) => setForm((p) => ({ ...p, inverse_relation: e.target.checked }))}
                                    />
                                    <label className="form-check-label" htmlFor="inverseCheck">
                                        Relação inversa (menor é melhor)
                                    </label>
                                </div>
                            </CCol>

                            <CCol xs={12}>
                                <CFormLabel>Observações</CFormLabel>
                                <CFormTextarea
                                    rows={4}
                                    value={form.obs}
                                    onChange={(e) => setForm((p) => ({ ...p, obs: e.target.value }))}
                                />
                            </CCol>

                            {(canCreate || canUpdate) && (
                                <>
                                    <CCol md={12} className="d-flex align-items-end">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="autoCheck"
                                                checked={!!form.auto}
                                                onChange={(e) => setForm((p) => ({ ...p, auto: e.target.checked }))}
                                            />
                                            <label className="form-check-label" htmlFor="autoCheck">
                                                Auto
                                            </label>
                                        </div>
                                    </CCol>

                                    <CCol md={6}>
                                        <CFormLabel>Key</CFormLabel>
                                        <CFormInput
                                            value={form.key}
                                            onChange={(e) => setForm((p) => ({ ...p, key: e.target.value }))}
                                            placeholder="ex: new_idi_projects_count"
                                        />
                                    </CCol>

                                    <CCol md={6}>
                                        <CFormLabel>Calc group</CFormLabel>
                                        <CFormInput
                                            value={form.calc_group}
                                            onChange={(e) => setForm((p) => ({ ...p, calc_group: e.target.value }))}
                                            placeholder="ex: new_idi_projects"
                                        />
                                    </CCol>
                                </>
                            )}
                        </CRow>
                    </CModalBody>

                    <CModalFooter>
                        <CButton color="secondary" variant="outline" onClick={closeModal} disabled={actionLoading}>
                            Cancelar
                        </CButton>
                        <CButton color="success" type="submit" disabled={actionLoading}>
                            {actionLoading ? <CSpinner size="sm" className="me-2" /> : null}
                            Guardar
                        </CButton>
                    </CModalFooter>
                </CForm>
            </CModal>

            {/* Upload payment days modal */}
            <CModal visible={uploadModalOpen} onClose={closeUpload} backdrop="static" alignment="center" size="lg">
                <CModalHeader>
                    <CModalTitle>Calcular dias de recebimento</CModalTitle>
                </CModalHeader>

                <CForm onSubmit={submitUpload}>
                    <CModalBody>
                        {errorText && <CAlert color="danger">{errorText}</CAlert>}
                        <div className="small text-body-secondary mb-3">
                            Indicador: {uploadTarget?.description || '-'}
                        </div>

                        <CRow className="g-3">
                            <CCol xs={12}>
                                <CFormLabel>Faturas (1 ou mais ficheiros)</CFormLabel>
                                <CFormInput
                                    type="file"
                                    accept=".xlsx,.xls"
                                    multiple
                                    onChange={(e) => setFaturasFiles(Array.from(e.target.files || []))}
                                />
                            </CCol>

                            <CCol xs={12}>
                                <CFormLabel>Recibos (1 ou mais ficheiros)</CFormLabel>
                                <CFormInput
                                    type="file"
                                    accept=".xlsx,.xls"
                                    multiple
                                    onChange={(e) => setRecibosFiles(Array.from(e.target.files || []))}
                                />
                            </CCol>
                        </CRow>
                    </CModalBody>

                    <CModalFooter>
                        <CButton color="secondary" variant="outline" onClick={closeUpload} disabled={uploadLoading}>
                            Cancelar
                        </CButton>
                        <CButton color="info" type="submit" disabled={uploadLoading}>
                            {uploadLoading ? <CSpinner size="sm" className="me-2" /> : null}
                            Calcular
                        </CButton>
                    </CModalFooter>
                </CForm>
            </CModal>

            {/* Delete confirm modal */}
            <CModal visible={!!deleteTarget} onClose={() => setDeleteTarget(null)} alignment="center" backdrop="static">
                <CModalHeader>
                    <CModalTitle>Apagar indicador</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    {deleteTarget ? (
                        <>
                            <p className="mb-2">Tens a certeza que queres apagar este indicador?</p>
                            <div className="small text-body-secondary">Ano: {deleteTarget.year}</div>
                            <div className="small text-body-secondary">Grupo: {deleteTarget.group}</div>
                            <div className="fw-semibold">{deleteTarget.description}</div>
                        </>
                    ) : null}
                </CModalBody>
                <CModalFooter>
                    <CButton color="secondary" variant="outline" onClick={() => setDeleteTarget(null)} disabled={actionLoading}>
                        Cancelar
                    </CButton>
                    <CButton
                        color="danger"
                        onClick={() => deleteRow(deleteTarget.id)}
                        disabled={actionLoading || !deleteTarget?.id}
                    >
                        {actionLoading ? <CSpinner size="sm" className="me-2" /> : <CIcon icon={cilTrash} className="me-2" />}
                        Apagar
                    </CButton>
                </CModalFooter>
            </CModal>
        </CRow>
    )
}
